import { useState, useEffect } from "react";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import SubmissionService from "../services/submissionService";
import CompetitionService from "../services/competitionService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faSpinner, faExclamationTriangle, faPlus, faPen, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";

const STATUS_CONFIG = {
  pending: { icon: faClock, color: 'text-grey-667', bg: 'bg-grey-200', label: 'Pending' },
  in_progress: { icon: faSpinner, color: 'text-primary', bg: 'bg-primary-bg', label: 'In Progress' },
  completed: { icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light', label: 'Completed' },
  delayed: { icon: faExclamationTriangle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Delayed' },
};

export default function Implementation() {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateContent, setUpdateContent] = useState('');
  const [submittingUpdate, setSubmittingUpdate] = useState(false);
  const [updatingMilestone, setUpdatingMilestone] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ progressNotes: '', budgetSpent: '' });

  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', targetDate: '', budgetSpent: '' });
  const [addingMilestone, setAddingMilestone] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      const { data } = await SubmissionService.getMySubmission(compData.data.competition._id);
      setSubmission(data.data.submission);
    } catch { /* */ }
    finally { setLoading(false); }
  };

  const toggleMilestoneComplete = async (m) => {
    const nextStatus = m.status === 'completed' ? 'in_progress' : 'completed';
    setUpdatingMilestone(m._id);
    try {
      await api.put(`/submissions/${submission._id}/milestones/${m._id}`, { status: nextStatus });
      await loadData();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to update');
    } finally { setUpdatingMilestone(null); }
  };

  const startEditing = (m) => {
    setEditingId(m._id);
    setEditDraft({
      progressNotes: m.progressNotes || '',
      budgetSpent: m.budgetSpent != null ? String(m.budgetSpent) : ''
    });
  };

  const saveEdit = async (m) => {
    setUpdatingMilestone(m._id);
    try {
      await api.put(`/submissions/${submission._id}/milestones/${m._id}`, {
        progressNotes: editDraft.progressNotes,
        budgetSpent: editDraft.budgetSpent === '' ? 0 : parseFloat(editDraft.budgetSpent)
      });
      setEditingId(null);
      await loadData();
      Success('Milestone updated');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to save');
    } finally { setUpdatingMilestone(null); }
  };

  const handleAddMilestone = async (e) => {
    e.preventDefault();
    if (!newMilestone.title.trim()) { ToastError('Milestone title required'); return; }
    setAddingMilestone(true);
    try {
      await api.post(`/submissions/${submission._id}/milestones`, {
        title: newMilestone.title.trim(),
        description: newMilestone.description.trim(),
        targetDate: newMilestone.targetDate || undefined,
        budgetSpent: newMilestone.budgetSpent === '' ? 0 : parseFloat(newMilestone.budgetSpent)
      });
      setNewMilestone({ title: '', description: '', targetDate: '', budgetSpent: '' });
      setShowAddMilestone(false);
      await loadData();
      Success('Milestone added');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to add milestone');
    } finally { setAddingMilestone(false); }
  };

  const handleMonthlyUpdate = async (e) => {
    e.preventDefault();
    if (!updateContent.trim()) return;
    setSubmittingUpdate(true);
    try {
      await api.post(`/submissions/${submission._id}/monthly-update`, { content: updateContent });
      Success('Monthly update submitted');
      setUpdateContent('');
      setShowUpdateForm(false);
      loadData();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to submit');
    } finally { setSubmittingUpdate(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  if (!submission || submission.currentRound !== 'implementation') {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <H1Title size="sm">Implementation Tracking</H1Title>
        <Paragraph className="text-grey-667 mt-2">
          Implementation tracking becomes available once your project is selected and enters the implementation phase.
        </Paragraph>
      </div>
    );
  }

  const impl = submission.implementation || {};
  const milestones = impl.milestones || [];
  const updates = impl.monthlyUpdates || [];
  const completedCount = milestones.filter(m => m.status === 'completed').length;
  const totalBudgetSpent = milestones.reduce((sum, m) => sum + (m.budgetSpent || 0), 0);
  const proposedBudget = submission.proposal?.budget?.totalAmount || 0;

  return (
    <div className="space-y-6 max-w-3xl">
      <H1Title size="sm">Implementation Tracking</H1Title>

      {/* Overview Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold text-primary">{completedCount}/{milestones.length}</p>
          <p className="text-xs text-grey-667">Milestones Done</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold">${totalBudgetSpent.toLocaleString()}</p>
          <p className="text-xs text-grey-667">Budget Spent{proposedBudget ? ` / $${proposedBudget.toLocaleString()}` : ''}</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold">{updates.length}</p>
          <p className="text-xs text-grey-667">Monthly Updates</p>
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Milestones</h3>
          <AppButton
            onClick={() => setShowAddMilestone(!showAddMilestone)}
            variant="outline"
            size="sm"
            fullWidth={false}
            className="px-4"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" /> {showAddMilestone ? 'Cancel' : 'Add Milestone'}
          </AppButton>
        </div>

        {showAddMilestone && (
          <form onSubmit={handleAddMilestone} className="border border-primary/30 bg-primary-bg/20 rounded-lg p-4 mb-4 space-y-3">
            <input
              type="text"
              placeholder="Milestone title (e.g. 'Distribute first batch of water filters')"
              className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary bg-white"
              value={newMilestone.title}
              onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
            />
            <textarea
              placeholder="Description (optional)"
              className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary resize-y min-h-[60px] bg-white"
              value={newMilestone.description}
              onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-grey-667 mb-1">Target date</label>
                <input
                  type="date"
                  className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white"
                  value={newMilestone.targetDate}
                  onChange={(e) => setNewMilestone({ ...newMilestone, targetDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs text-grey-667 mb-1">Budget spent ($, optional)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white"
                  value={newMilestone.budgetSpent}
                  onChange={(e) => setNewMilestone({ ...newMilestone, budgetSpent: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <AppButton type="submit" loading={addingMilestone} size="sm" fullWidth={false} className="px-4">
                Add Milestone
              </AppButton>
            </div>
          </form>
        )}

        {milestones.length === 0 && !showAddMilestone ? (
          <p className="text-sm text-grey-667 text-center py-6">No milestones yet. Click <strong>Add Milestone</strong> above to log your first one.</p>
        ) : (
          <div className="space-y-3">
            {milestones.map((m) => {
              const config = STATUS_CONFIG[m.status] || STATUS_CONFIG.pending;
              const isEditing = editingId === m._id;
              const busy = updatingMilestone === m._id;
              const done = m.status === 'completed';

              return (
                <div key={m._id} className="border border-grey-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleMilestoneComplete(m)}
                      disabled={busy}
                      title={done ? 'Mark as incomplete' : 'Mark as completed'}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer transition-colors ${
                        done ? 'bg-green border-green' : 'bg-white border-grey-200 hover:border-primary'
                      }`}
                    >
                      {done && <FontAwesomeIcon icon={faCheckCircle} className="text-white text-xs" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`font-semibold text-sm ${done ? 'line-through text-grey-98' : ''}`}>{m.title}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>{config.label}</span>
                          {!isEditing && (
                            <button onClick={() => startEditing(m)} className="text-grey-98 hover:text-primary cursor-pointer" title="Edit notes / budget">
                              <FontAwesomeIcon icon={faPen} className="text-xs" />
                            </button>
                          )}
                        </div>
                      </div>
                      {m.description && <p className="text-xs text-grey-667 mt-1">{m.description}</p>}
                      {m.targetDate && (
                        <p className="text-xs text-grey-98 mt-1">Target: {new Date(m.targetDate).toLocaleDateString()}</p>
                      )}

                      {isEditing ? (
                        <div className="mt-3 bg-grey-bg rounded-md p-3 space-y-2">
                          <div>
                            <label className="block text-xs text-grey-667 mb-1">Progress notes</label>
                            <textarea
                              className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white resize-y min-h-[60px]"
                              placeholder="What progress did you make on this milestone?"
                              value={editDraft.progressNotes}
                              onChange={(e) => setEditDraft({ ...editDraft, progressNotes: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-grey-667 mb-1">Budget spent ($) — on what was it spent?</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white"
                              placeholder="0"
                              value={editDraft.budgetSpent}
                              onChange={(e) => setEditDraft({ ...editDraft, budgetSpent: e.target.value })}
                            />
                            <p className="text-[10px] text-grey-98 mt-1">Detail the spend in the progress notes above so the admin has context.</p>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditingId(null)} className="text-xs text-grey-667 hover:text-grey-dark px-3 py-1.5 cursor-pointer">
                              <FontAwesomeIcon icon={faTimes} className="mr-1" /> Cancel
                            </button>
                            <AppButton onClick={() => saveEdit(m)} loading={busy} size="sm" fullWidth={false} className="px-3">
                              <FontAwesomeIcon icon={faSave} className="mr-1" /> Save
                            </AppButton>
                          </div>
                        </div>
                      ) : (
                        <>
                          {m.progressNotes && (
                            <p className="text-xs text-grey-667 mt-2 italic">"{m.progressNotes}"</p>
                          )}
                          {m.budgetSpent > 0 && (
                            <p className="text-xs text-grey-dark mt-1">Spent: <strong>${m.budgetSpent.toLocaleString()}</strong></p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Monthly Updates */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Monthly Updates</h3>
          <AppButton onClick={() => setShowUpdateForm(!showUpdateForm)} variant="outline" size="sm" fullWidth={false} className="px-4">
            <FontAwesomeIcon icon={faPlus} className="mr-1" /> New Update
          </AppButton>
        </div>

        {showUpdateForm && (
          <form onSubmit={handleMonthlyUpdate} className="border border-grey-200 rounded-lg p-4 mb-4 space-y-3">
            <textarea
              className="w-full border border-grey-200 rounded-md p-3 text-sm min-h-[120px] outline-none focus:border-primary resize-y"
              placeholder="Describe your progress this month: milestones hit, community engagement, blockers, budget spend..."
              value={updateContent}
              onChange={(e) => setUpdateContent(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <AppButton type="button" variant="outline" onClick={() => setShowUpdateForm(false)} fullWidth={false} size="sm" className="px-4">Cancel</AppButton>
              <AppButton type="submit" size="sm" fullWidth={false} className="px-4" loading={submittingUpdate}>Submit Update</AppButton>
            </div>
          </form>
        )}

        {updates.length === 0 ? (
          <p className="text-sm text-grey-667 text-center py-6">No monthly updates submitted yet</p>
        ) : (
          <div className="space-y-3">
            {[...updates].reverse().map((u, i) => (
              <div key={i} className="border-l-2 border-primary pl-4 py-2">
                <p className="text-xs text-grey-98 mb-1">
                  {new Date(u.submittedAt || u.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-sm text-grey-dark whitespace-pre-wrap">{u.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
